import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  LayoutDashboard, Building2, Layers3, BadgeDollarSign, CreditCard,
  Boxes, LogOut, KeyRound, Users
} from "lucide-react";

const MENU = [
  { to: "/plataforma", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/plataforma/empresas", label: "Empresas", icon: Building2 },
  { to: "/plataforma/planos", label: "Planos", icon: Layers3 },
  { to: "/plataforma/assinaturas", label: "Assinaturas", icon: BadgeDollarSign },
  { to: "/plataforma/pagamentos", label: "Pagamentos", icon: CreditCard },
  { to: "/plataforma/modulos", label: "Módulos", icon: Boxes },
  { to: "/plataforma/equipe", label: "Equipe da plataforma", icon: Users },
];

export function PlatformLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menu = MENU.filter(m => user?.is_platform_owner || ["/plataforma/empresas", "/plataforma/planos", "/plataforma/assinaturas", "/plataforma/pagamentos", "/plataforma/modulos"].includes(m.to));
  const [pwdOpen, setPwdOpen] = useState(false);

  return (
    <div className="min-h-screen almox-bg text-slate-100">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col glass border-r border-slate-800 z-30">
        <div className="px-4 py-5 border-b border-slate-800">
          <Brand />
          <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-violet-300 font-mono">
            Administração da Plataforma
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-3 overflow-y-auto">
          {menu.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-violet-900/40 text-violet-100 border border-violet-700/50"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border border-transparent"
                }`
              }
            >
              <m.icon className="h-[18px] w-[18px] shrink-0" />
              <span>{m.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800 space-y-2">
          <div className="rounded-lg bg-slate-900/60 px-3 py-2">
            <div className="text-sm font-medium text-slate-100 truncate">{user?.name}</div>
            <div className="text-[10px] text-violet-300 font-mono uppercase">{user?.is_platform_owner ? "Proprietário" : "Equipe da plataforma"}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" className="flex-1 justify-start text-slate-400" onClick={() => setPwdOpen(true)}>
              <KeyRound className="h-4 w-4 mr-2" /> Senha
            </Button>
            <Button variant="ghost" className="flex-1 justify-start text-slate-400 hover:text-rose-300" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="lg:hidden sticky top-0 z-20 glass border-b border-slate-800 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <Brand size="sm" />
            <Button size="sm" variant="ghost" onClick={logout}><LogOut className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {menu.map((m) => (
              <NavLink key={m.to} to={m.to} end={m.end}
                className={({ isActive }) => `shrink-0 rounded-lg px-3 py-2 text-xs border ${
                  isActive ? "bg-violet-900/40 border-violet-700/50 text-violet-100" : "border-slate-800 text-slate-400"
                }`}>
                {m.label}
              </NavLink>
            ))}
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </div>
  );
}
